import React from "react";
import ReactSelect, {
  components,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import styled, { useTheme } from "styled-components";
import { Box, BoxProps } from "../Box";
import { Label } from "../Label";
import { Paragraph } from "../Paragraph";

export type Option = {
  label: string;
  value: string | number | boolean;
  /** react-select greys the option out and won't let it be picked. */
  isDisabled?: boolean;
};

interface SelectProps extends BoxProps {
  options?: Option[];
  onChange: (newValue: Option | Option[]) => void;
  value: Option | Option[] | null | undefined | "" | number | string;
  placeholder?: string;
  color?: string;
  label?: string;
  size?: "regular" | "large";
  name?: string;
  /** Set on the select's input, as a stable locator for tests. */
  id?: string;
  initialValue?: Option | Option[];
  isDisabled?: boolean;
  isInModal?: boolean;
  isRequired?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  getOptionLabel?: any;
  menuIsOpen?: boolean;
  onMenuClose?: () => void;
  isMulti?: boolean; // Prop to enable multi-select
  /**
   * With `isMulti`: show a checkbox per option, keep the menu open while
   * picking, and summarise the selection ("First +2") instead of chips so the
   * control stays one line high.
   */
  checkboxMulti?: boolean;
  isLoading?: boolean;
  /** Native tooltip — useful for explaining why a disabled field is disabled. */
  title?: string;
}

const OptionCheckbox = styled.input`
  margin-right: ${({ theme }) => theme.space[1]};
  pointer-events: none;
`;

const Summary = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CheckboxOption = (props: OptionProps<Option, true>) => (
  <components.Option {...props}>
    <OptionCheckbox
      type="checkbox"
      checked={props.isSelected}
      readOnly
      tabIndex={-1}
    />
    {props.label}
  </components.Option>
);

const SummaryValueContainer = ({
  children,
  ...props
}: ValueContainerProps<Option, true>) => {
  const selected = props.getValue();
  // children is [chips[] | placeholder, input]. Don't flatten it: the input
  // must render, or blur (close on outside click) never fires.
  const [valuesOrPlaceholder, input] = children as React.ReactNode[];
  const showSummary = selected.length > 0 && !props.selectProps.inputValue;

  return (
    <components.ValueContainer {...props}>
      {selected.length === 0 && valuesOrPlaceholder}
      {showSummary && (
        <Summary>
          {selected[0].label}
          {selected.length > 1 ? ` +${selected.length - 1}` : ""}
        </Summary>
      )}
      {input}
    </components.ValueContainer>
  );
};

const Select = (props: SelectProps) => {
  const {
    options,
    onChange,
    value,
    placeholder,
    errorMessage,
    hasError,
    label,
    name,
    id,
    initialValue,
    isDisabled,
    size = "large",
    isInModal,
    isRequired,
    getOptionLabel,
    menuIsOpen,
    onMenuClose,
    isMulti = false, // Default to single select
    checkboxMulti = false,
    isLoading,
    ...styleProps
  } = props;

  const useCheckboxes = isMulti && checkboxMulti;

  const { colors, radii, space, fontSizes } = useTheme() as any;

  return (
    <Box {...styleProps}>
      {label && (
        <Label>
          {label}
          {isRequired ? " *" : ""}
        </Label>
      )}
      <ReactSelect
        options={options}
        isDisabled={isDisabled}
        isLoading={isLoading}
        onChange={onChange as any}
        value={initialValue ?? value}
        placeholder={placeholder}
        getOptionLabel={getOptionLabel}
        menuIsOpen={menuIsOpen}
        onMenuClose={onMenuClose}
        name={name}
        inputId={id}
        isMulti={isMulti} // Enable multi-select based on the prop
        {...(useCheckboxes && {
          closeMenuOnSelect: false,
          hideSelectedOptions: false,
          // Chips are hidden, so backspace would silently drop a selection.
          backspaceRemovesValue: false,
          components: {
            Option: CheckboxOption,
            ValueContainer: SummaryValueContainer,
          },
        })}
        menuPortalTarget={isInModal ? document.body : null}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base, state) => ({
            ...base,
            border: `1px solid ${
              hasError
                ? colors.red.primary
                : state.isFocused
                ? colors.blue.primary
                : colors.gray[30]
            }`,
            borderRadius: radii[0],
            height: size === "regular" ? 40 : 44,
            minHeight: size === "regular" ? 40 : 44,
            paddingLeft: space[1],
            fontFamily: "Inter",
            fontSize: fontSizes[2],
            fontWeight: "400",
            boxShadow: state.isFocused
              ? `0 0 0 3px ${hasError ? colors.red[50] : colors.blue[50]}`
              : "none",
            transition: "border-color .15s ease, box-shadow .15s ease",
            "::placeholder": {
              color: colors.gray[50],
            },
            ":hover": {
              border: `1px solid ${
                state.isFocused ? colors.blue.primary : colors.gray[40]
              }`,
            },
          }),
          // Checkbox mode: summary + search input on one line, never wrapped,
          // so text and indicators stay vertically centred.
          valueContainer: (base) =>
            useCheckboxes
              ? {
                  ...base,
                  flexWrap: "nowrap",
                  alignItems: "center",
                  overflow: "hidden",
                }
              : base,
          input: (base) =>
            useCheckboxes ? { ...base, margin: 0, padding: 0 } : base,
          indicatorsContainer: (base) =>
            useCheckboxes ? { ...base, height: "100%" } : base,
          option: (base, { isSelected, isFocused }) => ({
            ...base,
            fontFamily: "Inter",
            height: size === "regular" ? 40 : 44,
            display: "flex",
            alignItems: "center",
            // The checkbox already marks selection; keep rows plain.
            fontWeight: isSelected && !useCheckboxes ? "bold" : "regular",
            backgroundColor:
              isFocused || (isSelected && !useCheckboxes)
                ? colors.background
                : colors.white,
            cursor: "pointer",
            color: colors.blue[100],
            ":hover": {
              backgroundColor: colors.background,
            },
          }),
        }}
      />

      {errorMessage && (
        <Paragraph color="red.primary" fontSize="2" mt="1" textAlign="right">
          {errorMessage}
        </Paragraph>
      )}
    </Box>
  );
};

export default Select;
